import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import $ from "jquery";
import moment from "moment";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DataTable from "datatables.net-bs5";
import { Box, Button, Menu, MenuItem, Paper, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "daterangepicker/daterangepicker.css";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import { domesticReportAPI } from "../services/domesticReportApi";
import { useAdminDashboardFilters } from "../hooks/useAdminDashboardFilters";
import {
  getDataTableExportRows,
  getDataTableSearchTerm,
  isDataTableFiltered,
} from "../utils/dataTableExportUtils";
import {
  getRoutePermissions,
  hasPermission,
  isDosAdminUser,
  isSystemAdminUser,
} from "../utils/auth";
import "./AdminDashboard.css";
import "./RaiseComplaint.css";
import "./UserManagement.css";
import "./DomesticReports.css";

export const DOMESTIC_REPORT_COLUMNS = [
  { key: "createdDate", label: "Created Date", align: "center" },
  { key: "complaintSrNumber", label: "SR Number", align: "center" },
  { key: "complaintStatus", label: "SR Status", align: "center" },
  { key: "productBrand", label: "Product/ Brand", align: "left" },
  { key: "customerName", label: "Customer Name", align: "left" },
  { key: "addressCityState", label: "Address/ City/ State", align: "left" },
  { key: "country", label: "Country", align: "left" },
  { key: "zone", label: "Zone", align: "left" },
  { key: "region", label: "Region", align: "left" },
  { key: "territory", label: "Territory", align: "left" },
  { key: "plantName", label: "Plant Name", align: "left" },
  { key: "invoiceNumber", label: "Invoice Number", align: "center" },
  { key: "complaintType", label: "Business Vertical", align: "left" },
  { key: "complaintCategory", label: "SR Category", align: "left" },
  { key: "actualTat", label: "Actual TAT", align: "right" },
  { key: "overallTat", label: "Overall TAT", align: "right" },
  { key: "claimQty", label: "Claim Qty", align: "right" },
  { key: "claimAmount", label: "Claim Amount (INR)", align: "right" },
  { key: "approvedQty", label: "Approved Qty", align: "right" },
  { key: "approvedAmount", label: "Approved Amount (INR)", align: "right" },
];

const DEFAULT_SELECTED_COLUMNS = DOMESTIC_REPORT_COLUMNS.map((c) => c.key);
const REPORT_FETCH_PAGE_SIZE = 10000;

/** "Initiator: 2d; BCM: 3d" → [{ role, days }] (assigned / recommended days). */
export const parseRoleWiseTat = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return [];
  return text
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = part.match(/^(.*?):\s*(-?\d+(?:\.\d+)?)\s*d?$/i);
      if (!match) return { role: part, days: "" };
      return { role: match[1].trim(), days: match[2] };
    })
    .filter((part) => part.role);
};

export const recommendedTatKey = (role) =>
  `recommendedTat__${String(role)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")}`;

export const collectRecommendedTatColumns = (rows) => {
  const roles = [];
  const seen = new Set();
  (rows || []).forEach((row) => {
    parseRoleWiseTat(row?.roleWiseTat).forEach(({ role }) => {
      const id = role.toLowerCase();
      if (seen.has(id)) return;
      seen.add(id);
      roles.push(role);
    });
  });
  return roles.map((role) => ({
    key: recommendedTatKey(role),
    label: `Recommended TAT - ${role}`,
    align: "right",
  }));
};

/** Grid columns with one Recommended TAT column per role, placed before Actual TAT. */
export const buildDomesticReportColumns = (rows) => {
  const recommended = collectRecommendedTatColumns(rows);
  const actualIndex = DOMESTIC_REPORT_COLUMNS.findIndex((col) => col.key === "actualTat");
  if (actualIndex < 0) return [...DOMESTIC_REPORT_COLUMNS, ...recommended];
  return [
    ...DOMESTIC_REPORT_COLUMNS.slice(0, actualIndex),
    ...recommended,
    ...DOMESTIC_REPORT_COLUMNS.slice(actualIndex),
  ];
};

export const decorateReportRows = (rows) =>
  (rows || []).map((row) => {
    const next = { ...row };
    parseRoleWiseTat(row?.roleWiseTat).forEach(({ role, days }) => {
      const key = recommendedTatKey(role);
      if (next[key] == null || next[key] === "") next[key] = days;
    });
    return next;
  });

export const isReportDayColumn = (key) =>
  key === "overallTat" ||
  key === "actualTat" ||
  key === "ageing" ||
  String(key || "").startsWith("recommendedTat__");
/** Chunk size for Excel/CSV/PDF export — avoids one huge SP call on large Export markets. */
const EXPORT_PAGE_SIZE = 2000;

const ALIGN_CLASS = {
  center: "text-center",
  left: "text-start",
  right: "text-end",
};

const getColumnClassName = (key) => {
  if (String(key || "").startsWith("recommendedTat__")) return "text-end";
  const col = DOMESTIC_REPORT_COLUMNS.find((c) => c.key === key);
  return ALIGN_CLASS[col?.align || "left"] || "text-start";
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatRoleWiseTatTooltipHtml = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const parts = text
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length <= 1) return escapeHtml(text);
  return parts.map((part) => escapeHtml(part)).join("<br>");
};

const TOOLTIP_COLUMN_KEYS = new Set([
  "customerName",
  "addressCityState",
  "country",
  "zone",
  "region",
  "territory",
]);

const renderTooltipCell = (key, data, type) => {
  if (type !== "display") return data ?? "";
  const text = formatCellValue(key, data);
  if (!text) return "";
  const display = escapeHtml(text);
  const tooltipHtml =
    key === "roleWiseTat" ? formatRoleWiseTatTooltipHtml(text) : display;
  const cellClass =
    key === "roleWiseTat" ? "sr-report-tat-cell" : "sr-report-tooltip-cell";
  return `<span class="${cellClass} sr-report-tip"
    data-bs-toggle="tooltip"
    data-bs-placement="top"
    data-bs-html="true"
    data-bs-title="${tooltipHtml}"
    title="${display}">${display}</span>`;
};

const renderRoleWiseTatCell = (data, type) =>
  renderTooltipCell("roleWiseTat", data, type);

const initReportTooltips = (tableEl) => {
  if (!tableEl || typeof window === "undefined" || !window.bootstrap?.Tooltip) return;
  tableEl.querySelectorAll('[data-bs-toggle="tooltip"].sr-report-tip').forEach((el) => {
    const existing = window.bootstrap.Tooltip.getInstance(el);
    if (existing) existing.dispose();
    new window.bootstrap.Tooltip(el, {
      html: true,
      placement: "top",
      container: "body",
      customClass: "sr-report-cell-tooltip",
      trigger: "hover focus",
    });
  });
};

const dedupeReportRows = (items) => {
  if (!Array.isArray(items) || !items.length) return [];
  const seen = new Set();
  return items.filter((row) => {
    const key = [
      String(row.srCode ?? "").trim(),
      String(row.childSrNumber ?? "").trim(),
      String(row.invoiceNumber ?? "").trim(),
      String(row.complaintSrNumber ?? "").trim(),
    ].join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const SEARCH_ICON_SVG = `<svg class="icon-right" xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24" aria-hidden="true" style="position:absolute;right:12px;top:50%;transform:translateY(-50%);pointer-events:none;width:16px;height:16px;color:#6c757d;fill:currentColor;"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>`;

const enhanceGridSearchBox = ($searchEl) => {
  $searchEl.each(function enhanceSearch() {
    const $box = $(this);
    if ($box.data("searchBoxEnhanced")) return;
    $box.data("searchBoxEnhanced", true);
    $box.addClass("srchDvwrpr dt-grid-search");

    const $input = $box.find("input[type='search'], input").first();
    if (!$input.length) return;

    $input
      .removeClass("form-control-sm")
      .addClass("form-control form-control-light text-start")
      .attr("placeholder", $input.attr("placeholder") || "Search...")
      .css({
        height: "42px",
        textAlign: "left",
        paddingRight: "40px",
        minWidth: "280px",
        width: "280px",
      });

    if (!$input.parent().hasClass("input-icon")) {
      $input.wrap(
        '<div class="input-icon" style="position:relative;display:inline-block;margin-left:8px;vertical-align:middle;"></div>'
      );
      $input.parent().append(SEARCH_ICON_SVG);
    }

    const $label = $box.find("label").first();
    if ($label.length) {
      $label
        .addClass("mb-0 d-inline-flex align-items-center")
        .css({ fontWeight: 500, color: "#333", gap: "0" });
      const labelText = $label.text().replace(/\s+/g, " ").trim();
      if (!labelText.toLowerCase().includes("search")) {
        $label.prepend(document.createTextNode("Search: "));
      }
    } else {
      $box.prepend('<span class="dt-grid-search-label">Search:</span>');
    }
  });
};

const setupDataTableLayout = (tableEl) => {
  if (!tableEl) return;
  try {
    const wrapper = $(tableEl).closest(".dataTables_wrapper, .dt-container");
    if (!wrapper.length) return;

    const lengthEl = wrapper.find(".dt-length, .dataTables_length");
    const searchEl = wrapper.find(".dt-search, .dataTables_filter");
    if (lengthEl.length || searchEl.length) {
      let topWrap = wrapper.find(".dtFltrTop");
      if (!topWrap.length) {
        topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
        wrapper.prepend(topWrap);
      }
      lengthEl.each(function moveLength() {
        $(this).appendTo(topWrap);
      });
      searchEl.each(function moveSearch() {
        $(this).appendTo(topWrap);
      });
      enhanceGridSearchBox(searchEl);
    }

    const infoEl = wrapper.find(".dt-info, .dataTables_info");
    const pagingEl = wrapper.find(".dt-paging, .dataTables_paginate");
    if (infoEl.length || pagingEl.length) {
      let bottomWrap = wrapper.find(".dtFltrBotm");
      if (!bottomWrap.length) {
        bottomWrap = $("<div class='dtFltrBotm d-flex flex-wrap'></div>");
        wrapper.append(bottomWrap);
      }
      infoEl.each(function moveInfo() {
        $(this).appendTo(bottomWrap);
      });
      pagingEl.each(function movePaging() {
        $(this).appendTo(bottomWrap);
      });
    }

    const dtScrollDiv = $(tableEl).closest(".dt-scroll");
    if (dtScrollDiv.length) {
      if (!dtScrollDiv.parent().hasClass("dtScrlWrprInner")) {
        dtScrollDiv.wrap('<div class="dtScrlWrprInner"></div>');
      }
      const outer = dtScrollDiv.parent().parent();
      if (outer.length) outer.addClass("dtScrlWrpr");
    } else {
      const container = $(tableEl).closest(".table-responsive");
      if (container.length) {
        container.find(".dt-scroll").each(function wrapScroll() {
          const $this = $(this);
          if (!$this.parent().hasClass("dtScrlWrprInner")) {
            $this.wrap('<div class="dtScrlWrprInner"></div>');
          }
          const outer = $this.parent().parent();
          if (outer.length) outer.addClass("dtScrlWrpr");
        });
      }
    }
  } catch (error) {
    console.error("Error setting up DataTable layout:", error);
  }
};

const formatCellValue = (key, value) => {
  if (value == null || value === "") return "";
  if (key === "createdDate") {
    const m = moment(value);
    return m.isValid() ? m.format("DD-MM-YYYY") : String(value);
  }
  if (
    ["claimQty", "claimAmount", "approvedQty", "approvedAmount"].includes(key) ||
    isReportDayColumn(key)
  ) {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    if (isReportDayColumn(key)) {
      return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
    }
    return key.includes("Amount")
      ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : n.toLocaleString("en-IN", { maximumFractionDigits: 3 });
  }
  return String(value);
};

function DomesticReports() {
  const location = useLocation();
  const navigate = useNavigate();
  const { canView } = getRoutePermissions(location.pathname);
  const canDownload = hasPermission("DOMESTIC_REPORTS", "DOWNLOAD");

  // Date range is available for all roles (including initiator).
  // Status remains system-admin only. SR Category is available for all user types.
  // DOS Admin: same unscoped data path (geo = All / 0000) but without admin-only Status filter.
  const isSystemAdmin = useMemo(() => isSystemAdminUser(), []);
  const isUnscopedReportViewer = useMemo(
    () => isSystemAdminUser() || isDosAdminUser(),
    []
  );

  const {
    defaultFY,
    filters,
    setFilters,
    filterExpanded,
    setFilterExpanded,
    filterDateRangePickerRef,
    statusOptions,
    srCategoryOptions,
    bvOptions,
    marketOptions,
    countryOptions,
    zoneOptions,
    regionOptions,
    territoryOptions,
    geoLoading,
    isExportCascade,
    exportCountryOptions,
    isGeoAllFilterValue,
    handleGeoFilterChange,
    buildFilterParams,
    handleResetFilters,
    marketDropdownDisabled,
  } = useAdminDashboardFilters({
    defaultMarket: "M001",
    scopeToUser: !isUnscopedReportViewer,
  });

  const tableContainerRef = useRef(null);
  const tableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const initialLoadDoneRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_SELECTED_COLUMNS);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const removedColumnKeysRef = useRef(new Set());

  const allColumns = useMemo(() => buildDomesticReportColumns(rows), [rows]);

  useEffect(() => {
    setSelectedColumns((prev) => {
      const allowed = new Set(allColumns.map((col) => col.key));
      const kept = prev.filter((key) => allowed.has(key));
      const keptSet = new Set(kept);
      allColumns.forEach((col) => {
        if (!keptSet.has(col.key) && !removedColumnKeysRef.current.has(col.key)) {
          keptSet.add(col.key);
        }
      });
      return allColumns.map((col) => col.key).filter((key) => keptSet.has(key));
    });
  }, [allColumns]);

  const visibleColumns = useMemo(
    () => allColumns.filter((col) => selectedColumns.includes(col.key)),
    [allColumns, selectedColumns]
  );

  const tableKey = useMemo(
    () => visibleColumns.map((c) => c.key).join("|"),
    [visibleColumns]
  );

  const destroyDataTable = useCallback(() => {
    const el = tableRef.current;
    if (!el) return;

    try {
      if (typeof window !== "undefined" && window.bootstrap?.Tooltip) {
        el.querySelectorAll('[data-bs-toggle="tooltip"].sr-report-tip').forEach((node) => {
          const instance = window.bootstrap.Tooltip.getInstance(node);
          if (instance) instance.dispose();
        });
      }
      if ($.fn.DataTable && $.fn.DataTable.isDataTable(el)) {
        $(el).DataTable().clear().destroy();
      } else if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.clear().destroy();
      }
    } catch (err) {
      console.warn("DataTable destroy warning", err);
    } finally {
      dataTableInstanceRef.current = null;
      el.innerHTML = "";
    }
  }, []);

  const buildParams = useCallback(
    (overrides = {}) => {
      const params = {
        ...buildFilterParams(overrides),
        pageNumber: 1,
        pageSize: overrides.pageSize ?? REPORT_FETCH_PAGE_SIZE,
      };
      // Always pass market (same as SR Detail Dump) — never call API without marketCode
      const marketCode = String(
        params.marketCode ||
          overrides.market ||
          filters.market ||
          "M001"
      ).trim();
      params.marketCode = marketCode || "M001";
      // Status remains system-admin only; SR Category applies for all user types.
      if (!isSystemAdmin) {
        delete params.statusCode;
      }
      return params;
    },
    [buildFilterParams, isSystemAdmin, filters.market]
  );

  const loadReport = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      setError("");
      try {
        const params = buildParams(overrides);
        const response = await domesticReportAPI.getData(params);
        const payload = response?.data ?? response?.Data ?? {};
        const items = payload.items || payload.Items || [];
        const uniqueItems = dedupeReportRows(items);
        setRows(decorateReportRows(uniqueItems));
        const apiTotal = Number(payload.totalRecords ?? payload.TotalRecords ?? 0) || 0;
        setTotalRecords(
          uniqueItems.length < items.length ? uniqueItems.length : apiTotal || uniqueItems.length
        );
      } catch (e) {
        console.error(e);
        setError("Failed to load report data.");
        setRows([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    if (canView === false) return;
    if (!filters.market) return;
    if (initialLoadDoneRef.current) return;

    // Wait until market options load and current market is valid for this user
    // (avoids first fetch with hardcoded M001 for Export-only assignees).
    if ((marketOptions || []).length > 0) {
      const marketOk = marketOptions.some(
        (o) =>
          String(o?.id || "").trim().toUpperCase() ===
          String(filters.market || "").trim().toUpperCase()
      );
      if (!marketOk) return;
    } else if (!isUnscopedReportViewer) {
      return;
    }

    // Scoped viewers: wait until geo/BV options exist so "All" expands to assigned codes
    if (!isUnscopedReportViewer) {
      const hasScopedCountry = (countryOptions || []).some(
        (o) => o?.id && String(o.id).trim() !== "0000"
      );
      const hasScopedZone = (zoneOptions || []).some(
        (o) => o?.id && String(o.id).trim() !== "0000"
      );
      const hasScopedBv = (bvOptions || []).length > 0;
      if (!hasScopedCountry && !hasScopedZone && !hasScopedBv) return;
    }

    initialLoadDoneRef.current = true;
    loadReport();
  }, [
    canView,
    filters.market,
    loadReport,
    isUnscopedReportViewer,
    marketOptions,
    countryOptions,
    zoneOptions,
    bvOptions,
  ]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    if (loading && rows.length === 0) return;
    if (!rows.length) return;

    destroyDataTable();
    container.innerHTML = "";

    const tbl = document.createElement("table");
    tbl.className = "table table-striped table-bordered complaint-table custmTblLst";
    tbl.style.width = "100%";
    container.appendChild(tbl);
    tableRef.current = tbl;

    try {
      if (!$.fn.DataTable) {
        DataTable($);
      }

      const dtColumns = [
        {
          title: "SL No",
          data: null,
          orderable: false,
          searchable: false,
          width: "56px",
          className: "text-center",
          render: (_data, _type, _row, meta) => meta.row + 1,
        },
        ...visibleColumns.map((col) => ({
          title: col.label,
          data: col.key,
          defaultContent: "",
          className: getColumnClassName(col.key),
          width:
            col.key === "roleWiseTat"
              ? "220px"
              : TOOLTIP_COLUMN_KEYS.has(col.key)
              ? "180px"
              : undefined,
          render:
            col.key === "roleWiseTat"
              ? renderRoleWiseTatCell
              : TOOLTIP_COLUMN_KEYS.has(col.key)
              ? (data, type) => renderTooltipCell(col.key, data, type)
              : col.key === "complaintSrNumber"
              ? (_data, type, row) => {
                  if (type !== "display") return _data ?? "";
                  const display = formatCellValue("complaintSrNumber", _data);
                  if (!display) return "";
                  const parentSr = row.srCode || row.complaintSrNumber || "";
                  const childSr = row.childSrNumber || "";
                  return `<a href="#" class="sr-report-link" data-parent-sr="${escapeHtml(
                    parentSr
                  )}" data-child-sr="${escapeHtml(childSr)}">${escapeHtml(display)}</a>`;
                }
              : (data) => formatCellValue(col.key, data),
        })),
      ];

      dataTableInstanceRef.current = $(tbl).DataTable({
        data: rows,
        columns: dtColumns,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [10, 25, 50, 100, 250, 500],
        dom: "lfrtip",
        ordering: true,
        scrollX: true,
        autoWidth: false,
        deferRender: true,
        pageLength: 50,
        initComplete: function () {
          setupDataTableLayout(tbl);
          initReportTooltips(tbl);
        },
        drawCallback: function () {
          initReportTooltips(tbl);
        },
      });

      $(tbl).on("click", ".sr-report-link", function (e) {
        e.preventDefault();
        const parentSr = $(this).data("parentSr") || "";
        const childSr = $(this).data("childSr") || "";
        if (!parentSr && !childSr) return;
        const parent = parentSr || childSr;
        const childQuery = childSr ? `childSrCode=${encodeURIComponent(childSr)}` : "";
        const fromQuery = "fromList=sr-report";
        const query = childQuery ? `?${childQuery}&${fromQuery}` : `?${fromQuery}`;
        navigate(`/complaints/${encodeURIComponent(parent)}${query}`);
      });
    } catch (err) {
      console.error("DataTable init error", err);
    }

    return () => {
      destroyDataTable();
      if (container) container.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, visibleColumns, loading, destroyDataTable, tableKey, navigate]);

  const handleFilter = () => {
    // Pass current filter snapshot so BV/geo selections are not lost to a stale closure
    loadReport({
      bv: Array.isArray(filters.bv) ? [...filters.bv] : [],
      country: Array.isArray(filters.country) ? [...filters.country] : [],
      zone: Array.isArray(filters.zone) ? [...filters.zone] : [],
      region: Array.isArray(filters.region) ? [...filters.region] : [],
      territory: Array.isArray(filters.territory) ? [...filters.territory] : [],
      market: filters.market,
      status: Array.isArray(filters.status) ? [...filters.status] : [],
      complaintCategory: filters.complaintCategory || "",
      dateRangeStart: filters.dateRangeStart,
      dateRangeEnd: filters.dateRangeEnd,
    });
  };

  const handleReset = () => {
    const resetParams = handleResetFilters();
    setSelectedColumns(DEFAULT_SELECTED_COLUMNS);
    loadReport(resetParams);
  };

  // Rows matching the grid's search (global + per column) when one is active,
  // otherwise null so the full data set is fetched from the API instead.
  const getSearchFilteredRows = () => {
    if (!isDataTableFiltered(dataTableInstanceRef)) return null;
    return {
      searchTerm: getDataTableSearchTerm(dataTableInstanceRef),
      rows: getDataTableExportRows(dataTableInstanceRef, rows),
    };
  };

  const marketLabel =
    marketOptions.find((m) => m.id === filters.market)?.text || filters.market || "All";

  const buildExportFileName = (extension) => {
    const marketTag = String(filters.market || "ALL").trim() || "ALL";
    return `SR_Report_${marketTag}_${moment().format("YYYYMMDD_HHmmss")}.${extension}`;
  };

  const triggerBlobDownload = (blob, fileName) => {
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Rows an export should write out. A search on the grid wins: only what the
  // user can see is exported. Otherwise pages are fetched in chunks (large data safe).
  const packExportRows = (items, extra) => {
    const decorated = decorateReportRows(dedupeReportRows(items));
    const exportColumns = buildDomesticReportColumns(decorated).filter((col) => {
      if (selectedColumns.includes(col.key)) return true;
      return (
        String(col.key).startsWith("recommendedTat__") &&
        !removedColumnKeysRef.current.has(col.key)
      );
    });
    return { items: decorated, exportColumns, ...extra };
  };

  const resolveExportData = async () => {
    const searchResult = getSearchFilteredRows();
    if (searchResult) {
      return packExportRows(searchResult.rows, {
        searchTerm: searchResult.searchTerm,
        isFiltered: true,
      });
    }

    const allItems = [];
    let pageNumber = 1;
    let total = Infinity;

    while (allItems.length < total) {
      const params = buildParams({ pageNumber, pageSize: EXPORT_PAGE_SIZE });
      const response = await domesticReportAPI.getData(params);
      const payload = response?.data ?? response?.Data ?? {};
      const items = payload.items || payload.Items || [];
      total =
        Number(payload.totalRecords ?? payload.TotalRecords ?? items.length) ||
        items.length;
      allItems.push(...items);
      if (!items.length || items.length < EXPORT_PAGE_SIZE) break;
      pageNumber += 1;
      if (pageNumber > 500) break;
    }

    return packExportRows(allItems, {
      searchTerm: "",
      isFiltered: false,
    });
  };

  const buildFilterNote = ({ isFiltered, searchTerm }, count) => {
    if (!isFiltered) return "";
    return searchTerm
      ? `Filtered by search: "${searchTerm}" (${count} records)`
      : `Filtered view (${count} records)`;
  };

  // Shared plumbing for the three export formats: resolve the rows once, then
  // hand them to the writer while keeping the busy state and errors consistent.
  const runExport = async (writer) => {
    if (!visibleColumns.length) {
      setError("Select at least one column to download.");
      return;
    }
    setDownloading(true);
    setError("");
    try {
      const exportData = await resolveExportData();
      if (!exportData.items.length) {
        setError("No records to download for the current filters.");
        return;
      }
      await writer(exportData);
    } catch (e) {
      console.error(e);
      setError("Failed to download report.");
    } finally {
      setDownloading(false);
    }
  };

  const exportToExcel = () =>
    runExport(async (exportData) => {
      const { items, exportColumns } = exportData;
      const columns = exportColumns?.length ? exportColumns : visibleColumns;
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("SR Report");
      sheet.mergeCells(1, 1, 1, columns.length);
      sheet.getCell(1, 1).value = `SR Report (${marketLabel})`;
      sheet.getCell(1, 1).font = { bold: true, size: 14 };

      const filterNote = buildFilterNote(exportData, items.length);
      if (filterNote) {
        sheet.mergeCells(2, 1, 2, columns.length);
        sheet.getCell(2, 1).value = filterNote;
        sheet.getCell(2, 1).font = { italic: true, size: 11, color: { argb: "FF666666" } };
      }

      const headerRow = sheet.getRow(3);
      headerRow.values = columns.map((c) => c.label);
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF667EEA" },
      };
      items.forEach((row) => {
        sheet.addRow(columns.map((c) => formatCellValue(c.key, row[c.key])));
      });
      columns.forEach((_, idx) => {
        sheet.getColumn(idx + 1).width = 18;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      triggerBlobDownload(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        buildExportFileName("xlsx")
      );
    });

  const exportToCSV = () =>
    runExport(async (exportData) => {
      const { items, exportColumns } = exportData;
      const columns = exportColumns?.length ? exportColumns : visibleColumns;
      const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
      const lines = [
        columns.map((c) => escape(c.label)).join(","),
        ...items.map((row) =>
          columns.map((c) => escape(formatCellValue(c.key, row[c.key]))).join(",")
        ),
      ];
      triggerBlobDownload(
        new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" }),
        buildExportFileName("csv")
      );
    });

  const exportToPDF = () =>
    runExport(async (exportData) => {
      const { items, exportColumns } = exportData;
      const columns = exportColumns?.length ? exportColumns : visibleColumns;
      const doc = new jsPDF("landscape");
      doc.setFontSize(14);
      doc.text(`SR Report (${marketLabel})`, 14, 14);

      const filterNote = buildFilterNote(exportData, items.length);
      if (filterNote) {
        doc.setFontSize(9);
        doc.setTextColor(102);
        doc.text(filterNote, 14, 20);
        doc.setTextColor(0);
      }

      autoTable(doc, {
        head: [columns.map((c) => c.label)],
        body: items.map((row) =>
          columns.map((c) => formatCellValue(c.key, row[c.key]))
        ),
        startY: filterNote ? 25 : 20,
        styles: { fontSize: 6, cellPadding: 1.5, overflow: "linebreak" },
        headStyles: { fillColor: [102, 126, 234], fontSize: 6 },
        margin: { top: 20, left: 8, right: 8 },
      });
      doc.save(buildExportFileName("pdf"));
    });

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportOption = (format) => {
    handleExportMenuClose();
    if (format === "excel") {
      exportToExcel();
    } else if (format === "csv") {
      exportToCSV();
    } else if (format === "pdf") {
      exportToPDF();
    }
  };

  if (canView === false) {
    return (
      <Box className="domestic-reports-page">
        <div className="alert alert-warning mb-0">
          You do not have permission to view SR Report.
        </div>
      </Box>
    );
  }

  return (
    <Box className="domestic-reports-page">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          className="service-request-heading"
          sx={{ color: "#333333", fontWeight: 600 }}
        >
          SR Report
        </Typography>
        {canDownload && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* Export Button with Dropdown */}
            <Button
              onClick={handleExportMenuOpen}
              disabled={downloading || loading || !visibleColumns.length}
              sx={{
                background: "transparent",
                border: "1px solid #667eea",
                color: "#667eea",
                fontWeight: 500,
                padding: "8px 10px",
                borderRadius: "6px",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "36px",
                height: "36px",
                "&:hover": {
                  background: "transparent",
                  borderColor: "#764ba2",
                  color: "#764ba2",
                },
                "& .MuiSvgIcon-root": {
                  color: "#667eea",
                },
                "&:hover .MuiSvgIcon-root": {
                  color: "#764ba2",
                },
              }}
              title={downloading ? "Downloading..." : "Export"}
            >
              <FileDownloadIcon
                sx={{ fontSize: "26px", filter: "drop-shadow(0 0 0.5px currentColor)" }}
              />
              <ArrowDropDownIcon sx={{ fontSize: "16px", ml: 0.5 }} />
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={handleExportMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem onClick={() => handleExportOption("excel")}>Excel</MenuItem>
              <MenuItem onClick={() => handleExportOption("csv")}>CSV</MenuItem>
              <MenuItem onClick={() => handleExportOption("pdf")}>PDF</MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

            <div
              className="card-panel mb-3 filter-panel"
              style={{ paddingBottom: filterExpanded ? "15px" : "2px" }}
            >
              <div
                className={`section-heading d-flex align-items-center gap-2 ${
                  filterExpanded ? "mb-3" : "filter-panel-heading-collapsed"
                }`}
                style={{
                  marginBottom: filterExpanded ? "14px" : 0,
                  cursor: "pointer",
                }}
                onClick={() => setFilterExpanded(!filterExpanded)}
              >
                <FilterListIcon
                  fontSize="small"
                  className="text-muted"
                  style={{ width: "18px", height: "18px", flexShrink: 0 }}
                />
                <span>Filter</span>
                <span className="filter-panel-active-daterange ms-auto">
                  {filters.dateRange || defaultFY.dateRange || "Select date range"}
                </span>
                {filterExpanded ? (
                  <ExpandLessIcon fontSize="small" className="text-muted" />
                ) : (
                  <ExpandMoreIcon fontSize="small" className="text-muted" />
                )}
              </div>

              {filterExpanded && (
                <div className="container-fluid">
                  <div className="row g-3 align-items-end">
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Select Date Range
                      </label>
                      <div className="input-icon" style={{ position: "relative" }}>
                        <input
                          type="text"
                          ref={filterDateRangePickerRef}
                          className="form-control form-control-light text-start"
                          placeholder="Select date range"
                          readOnly
                          style={{ height: "42px", textAlign: "left" }}
                        />
                        <CalendarTodayIcon
                          className="icon-right"
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            width: "16px",
                            height: "16px",
                            color: "#6c757d",
                          }}
                        />
                      </div>
                    </div>

                    {isSystemAdmin && (
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">Status</label>
                      <CustomMultiSelect
                        options={statusOptions}
                        value={filters.status || []}
                        onChange={(selected) =>
                          setFilters((prev) => ({
                            ...prev,
                            status: Array.isArray(selected) ? selected : [],
                          }))
                        }
                        placeholder="Select Status"
                      />
                    </div>
                    )}

                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">Market</label>
                      <select
                        className="form-select form-control-light filter-select text-start"
                        value={filters.market || "M001"}
                        disabled={!marketOptions.length || marketDropdownDisabled}
                        onChange={(e) => {
                          // Keep Domestic as fallback — never leave market empty (disables geo filters)
                          const nextMarket = e.target.value || "M001";
                          setFilters((prev) => ({
                            ...prev,
                            market: nextMarket,
                            complaintCategory: "",
                            country: [],
                            zone: [],
                            region: [],
                            territory: [],
                            bv: [],
                          }));
                        }}
                        style={{ height: "42px", textAlign: "left" }}
                      >
                        {marketOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.text}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Business Vertical
                      </label>
                      <CustomMultiSelect
                        options={bvOptions}
                        value={filters.bv || []}
                        onChange={(selected) =>
                          setFilters((prev) => ({
                            ...prev,
                            bv: Array.isArray(selected) ? selected : [],
                          }))
                        }
                        placeholder="Select Business Vertical"
                      />
                    </div>

                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        SR Category
                      </label>
                      <select
                        key={`sr-cat-${filters.market || "none"}`}
                        className="form-select form-control-light filter-select text-start"
                        style={{ height: "42px", textAlign: "left" }}
                        disabled={!filters.market}
                        value={filters.complaintCategory || ""}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            complaintCategory: e.target.value || "",
                          }))
                        }
                      >
                        <option value="">
                          {!filters.market ? "Select Market first" : "Select SR Category"}
                        </option>
                        {srCategoryOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.text}
                          </option>
                        ))}
                      </select>
                    </div>

                    {(isExportCascade ? ["zone", "country", "region", "territory"] : ["country", "zone", "region", "territory"]).map((geoKey) => {
                      const countryIsAll = isGeoAllFilterValue(filters.country);
                      const zoneIsAll = isGeoAllFilterValue(filters.zone);
                      const regionIsAll = isGeoAllFilterValue(filters.region);
                      if (geoKey === "country") {
                        const countryList = isExportCascade
                          ? exportCountryOptions
                          : countryOptions;
                        return (
                          <div key="country" className="col-12 col-md-6 col-lg-3">
                            <label className="input-label d-block mb-1 text-start">
                              Country
                            </label>
                            <CustomMultiSelect
                              options={countryList}
                              value={filters.country || []}
                              onChange={(selected) =>
                                handleGeoFilterChange("country", selected, countryList)
                              }
                              placeholder={
                                isExportCascade
                                  ? filters.zone?.length
                                    ? "Select Country"
                                    : "Select zone first"
                                  : "Select Country"
                              }
                              disabled={
                                !filters.market ||
                                (isExportCascade &&
                                  (!filters.zone?.length || zoneIsAll))
                              }
                              loading={geoLoading.country}
                              loadingText="Loading countries..."
                              showSelectAll={!countryIsAll && !zoneIsAll}
                            />
                          </div>
                        );
                      }
                      if (geoKey === "zone") {
                        return (
                          <div key="zone" className="col-12 col-md-6 col-lg-3">
                            <label className="input-label d-block mb-1 text-start">Zone</label>
                            <CustomMultiSelect
                              options={zoneOptions}
                              value={filters.zone || []}
                              onChange={(selected) =>
                                handleGeoFilterChange("zone", selected, zoneOptions)
                              }
                              placeholder={
                                !filters.market
                                  ? "Select market first"
                                  : isExportCascade
                                    ? "Select zone(s)"
                                    : filters.country?.length
                                      ? "Select zone(s)"
                                      : "Select country first"
                              }
                              disabled={
                                !filters.market ||
                                (!isExportCascade &&
                                  (!filters.country?.length || countryIsAll))
                              }
                              loading={geoLoading.zone}
                              loadingText="Loading zones..."
                              showSelectAll={!zoneIsAll && !countryIsAll}
                            />
                          </div>
                        );
                      }
                      if (geoKey === "region") {
                        const regionReady = isExportCascade
                          ? filters.zone?.length && filters.country?.length
                          : filters.zone?.length;
                        const lockedByAll = countryIsAll || zoneIsAll || regionIsAll;
                        return (
                          <div key="region" className="col-12 col-md-6 col-lg-3">
                            <label className="input-label d-block mb-1 text-start">
                              Region
                            </label>
                            <CustomMultiSelect
                              options={regionOptions}
                              value={filters.region || []}
                              onChange={(selected) =>
                                handleGeoFilterChange("region", selected, regionOptions)
                              }
                              placeholder={
                                regionReady
                                  ? "Select region(s)"
                                  : isExportCascade
                                    ? "Select country first"
                                    : "Select zone first"
                              }
                              disabled={
                                !filters.market || !regionReady || lockedByAll
                              }
                              loading={geoLoading.region}
                              loadingText="Loading regions..."
                              showSelectAll={!lockedByAll}
                            />
                          </div>
                        );
                      }
                      const territoryLockedByAll =
                        countryIsAll || zoneIsAll || regionIsAll;
                      return (
                        <div key="territory" className="col-12 col-md-6 col-lg-3">
                          <label className="input-label d-block mb-1 text-start">
                            Territory
                          </label>
                          <CustomMultiSelect
                            options={territoryOptions}
                            value={filters.territory || []}
                            onChange={(selected) =>
                              handleGeoFilterChange(
                                "territory",
                                selected,
                                territoryOptions
                              )
                            }
                            placeholder={
                              filters.region?.length
                                ? "Select territory(ies)"
                                : "Select region first"
                            }
                            disabled={
                              !filters.market ||
                              !filters.region?.length ||
                              territoryLockedByAll
                            }
                            loading={geoLoading.territory}
                            loadingText="Loading territories..."
                            showSelectAll={!territoryLockedByAll}
                            selectAllLabel={
                              territoryOptions.length > 0
                                ? `Select all territories (${territoryOptions.length})`
                                : "Select all territories"
                            }
                          />
                        </div>
                      );
                    })}

                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        <ViewColumnIcon fontSize="inherit" className="me-1" />
                        Report Columns
                      </label>
                      <CustomMultiSelect
                        options={allColumns.map((c) => ({
                          id: c.key,
                          text: c.label,
                        }))}
                        value={selectedColumns}
                        onChange={(selected) => {
                          const next = Array.isArray(selected) ? selected : [];
                          const chosen = next.length ? next : allColumns.map((col) => col.key);
                          const chosenSet = new Set(chosen);
                          removedColumnKeysRef.current = new Set(
                            allColumns.map((col) => col.key).filter((key) => !chosenSet.has(key))
                          );
                          setSelectedColumns(
                            allColumns.map((col) => col.key).filter((key) => chosenSet.has(key))
                          );
                        }}
                        placeholder="Select columns"
                        showSelectAll
                      />
                    </div>

                    <div className="col-12 col-md-6 col-lg-3">
                      <label
                        className="input-label d-block mb-1"
                        style={{ visibility: "hidden" }}
                      >
                        Actions
                      </label>
                      <div className="d-flex gap-2 filter-buttons align-items-center">
                        <button
                          type="button"
                          className="btn btn-filter btn-sm"
                          onClick={handleFilter}
                          style={{
                            background:
                              "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                            border: "none",
                            color: "white",
                            fontWeight: 600,
                            boxShadow: "0 2px 8px rgba(102, 126, 234, 0.4)",
                            borderRadius: "4px",
                            padding: "4px 12px",
                            fontSize: "0.875rem",
                          }}
                        >
                          <FilterListIcon fontSize="small" className="me-1" />
                          Filter
                        </button>
                        <button
                          type="button"
                          className="btn btn-reset-filter btn-sm"
                          onClick={handleReset}
                          title="Reset"
                          style={{
                            background:
                              "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                            border: "none",
                            color: "white",
                            fontWeight: 600,
                            boxShadow: "0 2px 8px rgba(132, 250, 176, 0.4)",
                            borderRadius: "4px",
                            padding: "4px 10px",
                            fontSize: "0.875rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <RestartAltIcon fontSize="small" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <Paper
          sx={{
            p: 0,
            backgroundColor: "transparent",
            boxShadow: "none",
            border: "none",
          }}
        >
          <div className="card-panel domestic-reports-grid-panel mt-0">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
              <Typography
                variant="body2"
                component="div"
                className="section-heading mb-0"
                sx={{ fontSize: "14px", fontWeight: 600 }}
              >
                Report Data
                <span className="text-muted ms-2" style={{ fontSize: "12px", fontWeight: 500 }}>
                  ({totalRecords.toLocaleString("en-IN")} records · {visibleColumns.length}{" "}
                  columns)
                </span>
              </Typography>
            </div>

            {loading && rows.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography sx={{ fontSize: "14px" }}>Loading report data...</Typography>
              </Box>
            ) : !loading && rows.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: "14px" }}>
                  No records found for the selected filters. Adjust filters and click Filter.
                </Typography>
              </Box>
            ) : (
              <div
                className="table-responsive domestic-reports-table-wrap"
                ref={tableContainerRef}
              />
            )}
          </div>
        </Paper>
    </Box>
  );
}

export default DomesticReports;
